import toast from 'react-hot-toast'
import { useLocation } from 'react-router'

import {
  type Query,
  setDataByPage,
  showApiError,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import { fvDate, fvDateUtc } from '@fv/dates'
import type {
  AddPaymentInput,
  AuditStatus,
  DisputeInvoiceInput,
  RemitPaymentsDto,
  UpdateAuditDto,
} from '@fv/models'

import { supportMessage } from '@/constants'
import { useRemoveFromList } from '@/hooks/shipments'
import { shipperFetch } from '@/utils/shipperFetch'

import {
  type AuditListCounts,
  auditKeys,
  buildCountsQueryFilter,
  type InfiniteAuditQueryData,
  updateAuditList,
  useApplyStatusChange,
} from './queries'
import type { Audit } from './types'

type PutInvoiceArgs = UpdateAuditDto & {
  loadId: string
}

const putInvoiceDetails = (loadId: string) => async (body: PutInvoiceArgs) =>
  shipperFetch(`/audit/${loadId}`, {
    method: 'PUT',
    body: {
      ...body,
      ...(body.invoiceDate && {
        invoiceDate: fvDate(body.invoiceDate).format('YYYY-MM-DD'),
      }),
    },
  })

export const useUpdateInvoice = (loadId: string) => {
  const queryClient = useQueryClient()
  return useMutation(putInvoiceDetails(loadId), {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: auditKeys.list('accepted') })
      toast.success('Updated invoice details')
    },
    onError(e) {
      showApiError('Error updating invoice', e)
    },
  })
}

const addLoadPayment =
  (loadId: string) =>
  async (args: AddPaymentInput): Promise<Audit> => {
    const amount = Number(args.paymentAmount)
    const invoiceAmount = Number(args.invoiceAmount)
    const invoiceNumber = args.invoiceNumber?.trim()
    const invoiceDate = args.invoiceDate
    const message = args.message?.trim()
    const includeInvoiceAmount =
      typeof invoiceAmount === 'number' && !isNaN(invoiceAmount)

    const body: AddPaymentInput = {
      paymentAmount: amount,
      invoiceDate,
      ...(includeInvoiceAmount && { invoiceAmount }),
      ...(invoiceNumber && { invoiceNumber }),
      ...(message && { message }),
    }

    return shipperFetch<Audit>(`/audit/${loadId}/payment`, {
      method: 'POST',
      body,
    })
  }

export function useAddPayment(audit: Audit, currentView: AuditStatus) {
  const applyStatusChange = useApplyStatusChange(currentView)

  const addPaymentMutation = useMutation(addLoadPayment(audit.loadId), {
    onSuccess: updatedAudit => {
      applyStatusChange({
        previous: audit,
        current: updatedAudit,
      })
    },
  })

  async function addPayment(args: AddPaymentInput) {
    return addPaymentMutation.mutateAsync(args).catch(() => {
      toast.error(`Unable to add payment, ${supportMessage}`)
    })
  }

  return {
    addPayment,
    isAddingPayment: addPaymentMutation.isPending,
  }
}

async function resetLoadAudit(loadId: string): Promise<Audit> {
  return shipperFetch<Audit>(`/audit/${loadId}`, { method: 'DELETE' })
}

export function useResetAudit(audit: Audit, currentView: AuditStatus) {
  const applyStatusChange = useApplyStatusChange(currentView)

  const resetAuditMutation = useMutation(resetLoadAudit, {
    onSuccess: updatedAudit => {
      applyStatusChange({
        previous: audit,
        current: updatedAudit,
      })
    },
  })

  function resetAudit(loadId: string) {
    resetAuditMutation.mutateAsync(loadId).catch(() => {
      toast.error(`Unable to reset audit, ${supportMessage}`)
    })
  }

  return {
    resetAudit,
    isResettingAudit: resetAuditMutation.isPending,
  }
}

const disputeLoadInvoice =
  (loadId: string) =>
  async (args: DisputeInvoiceInput): Promise<Audit> => {
    const amount = Number(args.amount)
    const emails = args.emails.map(e => e.trim().toLocaleLowerCase())
    const invoiceNumber = args.invoiceNumber?.trim()
    const message = args.message?.trim()
    const invoiceDate = args.invoiceDate
      ? fvDateUtc(args.invoiceDate).format('YYYY-MM-DD')
      : ''

    return shipperFetch<Audit>(`/audit/${loadId}/dispute`, {
      method: 'POST',
      body: {
        amount,
        emails,
        ...(invoiceNumber && { invoiceNumber }),
        ...(message && { message }),
        ...(invoiceDate && { invoiceDate }),
      },
    })
  }

export function useDisputeInvoice(audit: Audit, currentView: AuditStatus) {
  const applyStatusChange = useApplyStatusChange(currentView)

  const disputeInvoiceMutation = useMutation(disputeLoadInvoice(audit.loadId), {
    onSuccess: updatedAudit => {
      applyStatusChange({
        previous: audit,
        current: updatedAudit,
      })
    },
  })

  function disputeInvoice(args: DisputeInvoiceInput) {
    disputeInvoiceMutation.mutateAsync(args).catch(() => {
      toast.error(`Unable to dispute invoice, ${supportMessage}`)
    })
  }

  return {
    disputeInvoice,
    isDisputing: disputeInvoiceMutation.isPending,
  }
}

async function remitLoadPayments(dto: RemitPaymentsDto): Promise<string[]> {
  if (!dto.loadIds.length) throw new Error('Missing load ids')

  await shipperFetch('/audit/remit', {
    method: 'POST',
    body: {
      doExport: Boolean(dto.doExport),
      loadIds: dto.loadIds,
    },
  })

  return dto.loadIds
}

export function useRemitPayments() {
  const { search } = useLocation()
  const queryClient = useQueryClient()
  const removeFromList = useRemoveFromList()

  const remitPaymentsMutation = useMutation(remitLoadPayments, {
    onSuccess: loadIds => {
      loadIds.forEach(loadId => {
        removeFromList({
          loadId,
          queryFilter: [...auditKeys.list('accepted')],
        })
      })

      queryClient.invalidateQueries({ queryKey: auditKeys.list('archived') })
      queryClient.setQueriesData<AuditListCounts | undefined>(
        buildCountsQueryFilter(search),
        prev =>
          prev && {
            ...prev,
            accepted: prev.accepted - loadIds.length,
            archived: prev.archived + loadIds.length,
          },
      )

      // Invalidate count queries not set above
      const countsFilter = buildCountsQueryFilter(search)
      queryClient.invalidateQueries({
        queryKey: countsFilter.queryKey,
        predicate: (q: Query) => !countsFilter.predicate(q),
      })
    },
  })

  function remitPayments(dto: RemitPaymentsDto) {
    remitPaymentsMutation.mutateAsync(dto).catch(() => {
      toast.error(`Unable to remit payments, ${supportMessage}`)
    })
  }

  const isLoading = remitPaymentsMutation.isPending
  const isExporting = remitPaymentsMutation.variables?.doExport ?? false

  return {
    remitPayments,
    isArchiving: isLoading && !isExporting,
    isExporting: isLoading && isExporting,
  }
}

type AddNoteDTO = {
  loadId: string
  note: string
}

async function addAuditNote(dto: AddNoteDTO): Promise<Audit> {
  return shipperFetch<Audit>(`/audit/${dto.loadId}/notes`, {
    method: 'POST',
    body: { note: dto.note.trim() },
  })
}

export function useAddNote() {
  const queryClient = useQueryClient()

  return useMutation(addAuditNote, {
    onSuccess: updatedAudit => {
      queryClient.setQueriesData<InfiniteAuditQueryData>(
        { queryKey: auditKeys.list('disputed') },
        setDataByPage(audits => updateAuditList(audits, updatedAudit)),
      )
    },
  })
}

type RemoveDisputeDTO = {
  loadId: string
}

async function removeDispute(dto: RemoveDisputeDTO): Promise<Audit> {
  return shipperFetch<Audit>(`/audit/${dto.loadId}/dispute`, {
    method: 'DELETE',
  })
}

export function useRemoveDispute(audit: Audit, currentView: AuditStatus) {
  const applyStatusChange = useApplyStatusChange(currentView)

  const removeDisputeMutation = useMutation(removeDispute, {
    onSuccess: updatedAudit => {
      applyStatusChange({
        previous: audit,
        current: updatedAudit,
      })

      toast.success('Dispute removed successfully.')
    },
  })

  function removeInvoiceDispute(dto: RemoveDisputeDTO) {
    removeDisputeMutation.mutateAsync(dto).catch(() => {
      toast.error(`Unable to remove disputed amount, ${supportMessage}`)
    })
  }

  return {
    removeInvoiceDispute,
    isRemovingDispute: removeDisputeMutation.isPending,
  }
}

type ResendDisputeEmailDTO = {
  loadId: string
}

async function resendDispute(dto: ResendDisputeEmailDTO): Promise<Audit> {
  return shipperFetch<Audit>(`/audit/${dto.loadId}/resend-dispute-email`, {
    method: 'POST',
  })
}

export function useResendDisputeEmail() {
  const queryClient = useQueryClient()

  const resendDisputeMutation = useMutation(resendDispute, {
    onSuccess: audit => {
      queryClient.setQueriesData<InfiniteAuditQueryData>(
        { queryKey: auditKeys.list('disputed') },
        setDataByPage(audits => updateAuditList(audits, audit)),
      )

      toast.success('Dispute email resent.')
    },
  })

  function resendDisputeEmail(dto: ResendDisputeEmailDTO) {
    resendDisputeMutation.mutateAsync(dto).catch(() => {
      toast.error(`Unable to send dispute email, ${supportMessage}`)
    })
  }

  return {
    resendDisputeEmail,
    isResending: resendDisputeMutation.isPending,
  }
}
